import {
    ErDiagramData,
    ErEntity,
    ErRelationship,
    ErAttribute,
    ErConnection,
    ErHierarchy,
    ScriptRelationalData,
    ScriptTable,
    ScriptAttribute,
    RelationalDiagramData,
    RelationalTable,
    RelationalColumn,
    RelationalForeignKey
} from '../model/ErModel';
import { RubricItem, DesignTestsConfig } from '../model/DesignTestConfig';

/**
 * Extreu automàticament els elements avaluables d'una solució ER i genera o sincronitza la rúbrica
 */
export const extractErRubricItems = (sol: ErDiagramData, existingConfig?: DesignTestsConfig | null): RubricItem[] => {
    const items: RubricItem[] = [];
    const prevMap = new Map<string, RubricItem>();
    if (existingConfig?.items) {
        existingConfig.items.forEach((it: RubricItem) => prevMap.set(it.id, it));
    }

    const entities: ErEntity[] = sol.entities || [];
    const relationships: ErRelationship[] = sol.relationships || [];
    const attributes: ErAttribute[] = sol.attributes || [];
    const connections: ErConnection[] = sol.connections || [];
    const hierarchies: ErHierarchy[] = sol.hierarchies || [];

    // 1. Entitats
    entities.forEach((e: ErEntity) => {
        const id = `entity_${e.id || e.name}`;
        const prev = prevMap.get(id);
        items.push({
            id,
            category: 'entity',
            label: `Entitat "${e.name}"`,
            details: e.isWeak ? 'Entitat Dèbil' : 'Entitat Regular',
            enabled: prev ? prev.enabled : true,
            points: prev ? prev.points : 0,
            targetKey: e.id
        });
    });

    // 2. Relacions (nom i cardinalitat)
    relationships.forEach((r: ErRelationship) => {
        const id = `rel_${r.id || r.name}`;
        const prev = prevMap.get(id);
        items.push({
            id,
            category: 'relationship',
            label: `Relació "${r.name}"`,
            details: `Cardinalitat ${r.cardinality || '1:N'}${r.isWeak ? ' · Dèbil/Identificativa' : ''}`,
            enabled: prev ? prev.enabled : true,
            points: prev ? prev.points : 0,
            targetKey: r.id
        });
    });

    // 3. Atributs (associats a la seva entitat o relació)
    attributes.forEach((a: ErAttribute) => {
        const parentEntity = entities.find((e: ErEntity) => e.id === a.parentId);
        const parentRel = relationships.find((r: ErRelationship) => r.id === a.parentId);
        const parentName = parentEntity ? parentEntity.name : (parentRel ? parentRel.name : 'Element');

        const flags: string[] = [];
        if (a.isPK) flags.push('Clau Primària (PK)');
        if (a.isWeakPK) flags.push('Discriminador/Parcial');
        if (a.isMultivalued) flags.push('Multivaluat');
        if (a.isDerived) flags.push('Derivat');

        const id = `attr_${a.id || (parentName + '_' + a.name)}`;
        const prev = prevMap.get(id);
        items.push({
            id,
            category: 'attribute',
            label: `Atribut "${a.name}" a "${parentName}"`,
            details: flags.length > 0 ? flags.join(', ') : 'Atribut simple',
            enabled: prev ? prev.enabled : true,
            points: prev ? prev.points : 0,
            targetKey: a.id
        });
    });

    // 4. Connexions (connexió entre entitat i relació, i participació)
    connections.forEach((c: ErConnection, idx: number) => {
        const fromElem = entities.find((e: ErEntity) => e.id === c.from) || relationships.find((r: ErRelationship) => r.id === c.from);
        const toElem = entities.find((e: ErEntity) => e.id === c.to) || relationships.find((r: ErRelationship) => r.id === c.to);
        const fromName = fromElem ? fromElem.name : c.from;
        const toName = toElem ? toElem.name : c.to;

        const id = `conn_${c.id || `${fromName}_${toName}_${idx}`}`;
        const prev = prevMap.get(id);
        items.push({
            id,
            category: 'connection',
            label: `Connexió "${fromName}" ⟷ "${toName}"`,
            details: c.participation ? `Participació: ${c.participation}` : 'Connexió sense participació explícita',
            enabled: prev ? prev.enabled : true,
            points: prev ? prev.points : 0,
            targetKey: c.id
        });
    });

    // 5. Jerarquies
    hierarchies.forEach((h: ErHierarchy, idx: number) => {
        const superEnt = entities.find((e: ErEntity) => e.id === h.superEntityId);
        const subNames = (h.subEntityIds || []).map((sid: string) => {
            const ent = entities.find((e: ErEntity) => e.id === sid);
            return ent ? ent.name : sid;
        }).join(', ');

        const id = `hier_${h.id || idx}`;
        const prev = prevMap.get(id);
        items.push({
            id,
            category: 'hierarchy',
            label: `Jerarquia: "${superEnt?.name || 'Super'}" ➔ [${subNames}]`,
            details: `Cobertura: ${h.coverage === 't' ? 'Total' : 'Parcial'} · Disjunció: ${h.disjointness === 'd' ? 'Disjunta' : 'Solapada'}`,
            enabled: prev ? prev.enabled : true,
            points: prev ? prev.points : 0,
            targetKey: h.id
        });
    });

    return normalizeInitialPoints(items, existingConfig);
};

/**
 * Extreu elements avaluables d'un Script Relacional
 */
export const extractScriptRubricItems = (sol: ScriptRelationalData, existingConfig?: DesignTestsConfig | null): RubricItem[] => {
    const items: RubricItem[] = [];
    const prevMap = new Map<string, RubricItem>();
    if (existingConfig?.items) {
        existingConfig.items.forEach((it: RubricItem) => prevMap.set(it.id, it));
    }

    const tables: ScriptTable[] = sol.tables || [];

    tables.forEach((t: ScriptTable) => {
        // Taula
        const tableId = `table_${t.name}`;
        const prevT = prevMap.get(tableId);
        items.push({
            id: tableId,
            category: 'table',
            label: `Taula "${t.name}"`,
            details: `Definició de l'esquema de la taula`,
            enabled: prevT ? prevT.enabled : true,
            points: prevT ? prevT.points : 0,
            targetKey: t.name
        });

        // Atributs, PKs i FKs
        (t.attributes || []).forEach((a: ScriptAttribute) => {
            const attrId = `attr_${t.name}.${a.name}`;
            const prevA = prevMap.get(attrId);

            const detailsList: string[] = [];
            if (a.isPK) detailsList.push('Clau Primària (PK)');
            if (a.fkTargetTable) detailsList.push(`FK que apunta a "${a.fkTargetTable}"`);

            items.push({
                id: attrId,
                category: a.isPK ? 'pk' : (a.fkTargetTable ? 'fk' : 'column'),
                label: `Camp "${a.name}" a "${t.name}"`,
                details: detailsList.length > 0 ? detailsList.join(' · ') : 'Columna regular',
                enabled: prevA ? prevA.enabled : true,
                points: prevA ? prevA.points : 0,
                targetKey: `${t.name}.${a.name}`
            });
        });
    });

    return normalizeInitialPoints(items, existingConfig);
};

/**
 * Extreu elements avaluables d'un Model Relacional (Taules, columnes amb tipus, PKs i FKs)
 */
export const extractRelationalRubricItems = (sol: RelationalDiagramData, existingConfig?: DesignTestsConfig | null): RubricItem[] => {
    const items: RubricItem[] = [];
    const prevMap = new Map<string, RubricItem>();
    if (existingConfig?.items) {
        existingConfig.items.forEach((it: RubricItem) => prevMap.set(it.id, it));
    }

    const tables: RelationalTable[] = sol.tables || [];
    const fks: RelationalForeignKey[] = sol.foreignKeys || [];

    // 1. Taules i Columnes
    tables.forEach((t: RelationalTable) => {
        const tableId = `rel_table_${t.id || t.name}`;
        const prevT = prevMap.get(tableId);
        items.push({
            id: tableId,
            category: 'table',
            label: `Taula "${t.name}"`,
            details: `Existència de la taula al model`,
            enabled: prevT ? prevT.enabled : true,
            points: prevT ? prevT.points : 0,
            targetKey: t.name
        });

        (t.columns || []).forEach((c: RelationalColumn) => {
            const colId = `rel_col_${t.name}.${c.name}`;
            const prevC = prevMap.get(colId);

            const details: string[] = [];
            if (c.type) details.push(`Tipus: ${c.type}`);
            if (c.isPK) details.push('Clau Primària (PK)');

            items.push({
                id: colId,
                category: c.isPK ? 'pk' : 'column',
                label: `Columna "${c.name}" a "${t.name}"`,
                details: details.join(' · '),
                enabled: prevC ? prevC.enabled : true,
                points: prevC ? prevC.points : 0,
                targetKey: `${t.name}.${c.name}`
            });
        });
    });

    // 2. Claus Foranes (FK)
    fks.forEach((f: RelationalForeignKey, idx: number) => {
        const fkId = `rel_fk_${f.id || `${f.fromTable}.${f.fromColumn}_${f.toTable}`}`;
        const prevF = prevMap.get(fkId);
        items.push({
            id: fkId,
            category: 'fk',
            label: `FK "${f.fromTable}.${f.fromColumn}" ➔ "${f.toTable}.${f.toColumn || ''}"`,
            details: `Clau Forana correctament connectada`,
            enabled: prevF ? prevF.enabled : true,
            points: prevF ? prevF.points : 0,
            targetKey: fkId
        });
    });

    return normalizeInitialPoints(items, existingConfig);
};

/**
 * Si no hi ha punts configurats prèviament o la suma és 0, reparteix 100 punts equitativament entre els ítems actius
 */
export const normalizeInitialPoints = (items: RubricItem[], existingConfig?: DesignTestsConfig | null): RubricItem[] => {
    const activeItems = items.filter((it: RubricItem) => it.enabled);
    const sumPoints = activeItems.reduce((acc: number, it: RubricItem) => acc + (it.points || 0), 0);

    if (activeItems.length > 0 && (sumPoints === 0 || !existingConfig)) {
        const base = Math.floor(100 / activeItems.length);
        let remainder = 100 - (base * activeItems.length);

        return items.map((it: RubricItem) => {
            if (!it.enabled) {
                return { ...it, points: 0 };
            }
            const extra = remainder > 0 ? 1 : 0;
            if (remainder > 0) remainder--;
            return {
                ...it,
                points: base + extra
            };
        });
    }

    return items;
};

/**
 * Reparteix els 100 punts de forma equitativa entre els elements marcats com a avaluables (enabled = true)
 */
export const distributeEqually100 = (items: RubricItem[]): RubricItem[] => {
    const active = items.filter((it: RubricItem) => it.enabled);
    if (active.length === 0) return items.map((it: RubricItem) => ({ ...it, points: 0 }));

    const base = Math.floor(100 / active.length);
    let remainder = 100 - (base * active.length);

    return items.map((it: RubricItem) => {
        if (!it.enabled) return { ...it, points: 0 };
        const extra = remainder > 0 ? 1 : 0;
        if (remainder > 0) remainder--;
        return {
            ...it,
            points: base + extra
        };
    });
};
